if ( typeof GiftTiers !== 'function' ) {

	class GiftTiers extends HTMLElement {

		constructor() {
			super();
			this.options = JSON.parse(this.dataset.options);
			this.tiers = (this.options.tiers || []).map(t => ({
				threshold: Number(t.threshold),
				giftVariantId: this._gid(t.giftVariantId),
				// Fall back to the gift product's name when no custom label is set.
				valueLabel: t.valueLabel || t.giftTitle || ''
			})).filter(t => !Number.isNaN(t.giftVariantId));
			this.giftVariantIds = this.tiers.map(t => t.giftVariantId);
			this.excludedProductIds = (this.options.excludedProductIds || '')
				.split(',').filter(Boolean).map(Number);
			this.syncing = false;

			this._onCartUpdated = this._onCartUpdated.bind(this);
			// Re-sync on ANY cart change. Every add/remove/qty path in this theme
			// (product-form add, quick-buy, drawer qty, remove, our own gift add) updates the
			// header cart count via textContent on a stable element. Observing it catches them
			// all — including paths that never dispatch cart-updated or call refreshCart, and
			// unlike #AjaxCartForm (which the drawer re-render replaces) this element persists.
			this._countEl = document.querySelector('[data-header-cart-count]');
			if ( this._countEl ) {
				this._observer = new MutationObserver(this._onCartUpdated);
				this._observer.observe(this._countEl, { childList: true, characterData: true, subtree: true });
			}
			this.sync();
		}

		// Extract numeric variant id from a GID string or number.
		_gid(value) {
			if ( typeof value === 'number' ) return value;
			const m = String(value).match(/(\d+)\s*$/);
			return m ? Number(m[1]) : NaN;
		}

		_onCartUpdated() { this.sync(); }

		disconnectedCallback() {
			if ( this._observer ) {
				this._observer.disconnect();
			}
		}

		async sync() {
			if ( this.syncing ) return;
			this.syncing = true;
			try {
				const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
				const qualifying = window.GiftTiersLogic.computeQualifyingCents(
					cart.items, this.giftVariantIds, this.excludedProductIds
				);

				// Always paint this instance's bar (both drawer + page instances render).
				this._paint(qualifying);

				const diff = window.GiftTiersLogic.diffGiftLines(cart.items, this.tiers, qualifying);
				const hasWork = diff.toAdd.length > 0 || diff.toRemove.length > 0;

				// Only ONE instance mutates the cart at a time. The cart page renders two
				// <gift-tiers> (drawer + page); without a shared lock both would double-add.
				if ( hasWork && ! GiftTiers.mutating ) {
					GiftTiers.mutating = true;
					try {
						let mutated = false;
						for ( const rem of diff.toRemove ) {
							if ( await this._changeByKey(rem.key, 0) ) mutated = true;
						}
						for ( const add of diff.toAdd ) {
							if ( await this._addGift(add.variantId, add.threshold) ) mutated = true;
						}
						if ( mutated && typeof window.refreshCart === 'function' ) {
							window.refreshCart(); // re-renders; fresh instances re-sync once, find no diff
						}
					} finally {
						GiftTiers.mutating = false;
					}
				}
			} catch (e) {
				console.error('[gift-tiers]', e);
			} finally {
				this.syncing = false;
			}
		}

		// Returns true on success, false if Shopify rejected the add (e.g. 422 sold out).
		_addGift(variantId, threshold) {
			return fetch(`${window.Shopify.routes.root}cart/add.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					items: [{ id: variantId, quantity: 1, properties: { _gift_tier: String(threshold) } }]
				})
			}).then(r => r.json()).then(res => ! (res && res.status >= 400));
		}

		// Returns true on success, false on failure.
		_changeByKey(key, quantity) {
			return fetch(`${window.Shopify.routes.root}cart/change.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: key, quantity })
			}).then(r => r.json()).then(res => ! (res && res.status >= 400));
		}

		_paint(qualifyingCents) {
			const freeShip = this.options.freeShippingThreshold || 0;
			const thresholds = this.tiers.map(t => t.threshold).concat([freeShip]);
			const maxThreshold = Math.max.apply(null, thresholds);
			const fill = this.querySelector('[data-js-gift-fill]');
			if ( fill ) {
				const pct = maxThreshold > 0 ? Math.min(100, (qualifyingCents * 100) / maxThreshold) : 0;
				fill.style.width = `${pct}%`;
			}
			// Markers (free-shipping + gift tiers) are positioned along the track and flagged
			// reached once the fill passes them.
			this.querySelectorAll('[data-js-gift-marker]').forEach(marker => {
				const th = Number(marker.dataset.threshold);
				if ( maxThreshold > 0 ) {
					marker.style.left = `${Math.min(100, (th * 100) / maxThreshold)}%`;
				}
				marker.setAttribute('data-reached', qualifyingCents >= th ? 'true' : 'false');
			});
			// Legend rows mirror the markers' reached state.
			this.querySelectorAll('[data-js-gift-legend]').forEach(row => {
				const th = Number(row.dataset.threshold);
				row.setAttribute('data-reached', qualifyingCents >= th ? 'true' : 'false');
			});

			const msg = this.querySelector('[data-js-gift-message]');
			if ( msg ) {
				const locales = window.KROWN.settings.locales;
				const next = window.GiftTiersLogic.resolveNextMilestone(qualifyingCents, this.tiers, freeShip);
				if ( next && next.type === 'shipping' ) {
					msg.innerHTML = locales.gift_tiers_shipping_next_html
						.replace('{{ amount }}', this._formatMoney(next.remaining));
				} else if ( next ) {
					msg.innerHTML = locales.gift_tiers_next_html
						.replace('{{ label }}', (next.tier && next.tier.valueLabel) || '')
						.replace('{{ amount }}', this._formatMoney(next.remaining));
				} else {
					msg.innerHTML = locales.gift_tiers_all_unlocked;
				}
			}
		}

		_formatMoney(cents) {
			if ( typeof cents === 'string' ) {
				cents = cents.replace('.', '');
			}
			const format = window.KROWN.settings.shop_money_format || '{{ amount }}';
			const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

			function formatWithDelimiters(number, precision, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';
				if ( isNaN(number) || number === null ) {
					return 0;
				}
				number = (number / 100.0).toFixed(precision);
				const parts = number.split('.');
				const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
				const centsAmount = parts[1] ? decimal + parts[1] : '';
				return dollarsAmount + centsAmount + (window.KROWN.settings.iso_code || '');
			}

			const match = format.match(placeholderRegex);
			if ( !match ) {
				return format;
			}
			let value = '';
			switch ( match[1] ) {
				case 'amount': value = formatWithDelimiters(cents, 2); break;
				case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
				case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
				case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
				case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
				case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
				default: value = formatWithDelimiters(cents, 2);
			}
			return format.replace(placeholderRegex, value);
		}

	}

	GiftTiers.mutating = false;

	if ( typeof customElements.get('gift-tiers') == 'undefined' ) {
		customElements.define('gift-tiers', GiftTiers);
	}

}
